import type { ReactNode } from 'react';
import styled from 'styled-components';

import { media } from 'helpers/breakpoints';
import { OpenOutlineIc } from '@dsch/dd-icons';
import {
  GoogleReviews,
  ReviewList,
} from 'components/PageWrappers/AdDetails/types/';
import { InfoTitle } from 'components/Toolkit/InfoTitle/InfoTitle';
import { SimpleShowMoreInline } from 'components/Toolkit/SimpleShowMore/SimpleShowMoreInline';
import { Link as ToolkitLink } from 'components/Toolkit/Button/Link';
import { StarRatingGroup } from 'components/PageWrappers/AdDetails/components/GoogleReviews/StarRatingGroup/StarRatingGroup';

interface GoogleReviewPanelProps {
  title: ReactNode;
  googleReviews: GoogleReviews;
  cdnUrl?: string;
  adId?: number;
  reviewUrl?: boolean;
  numberReviews?: number;
}

export const GoogleReviewContainer = styled.div`
  padding: ${({ theme }) =>
    `0 ${theme.spacing.M16} ${theme.spacing.M16} ${theme.spacing.M16}`};
  scroll-margin: ${({ theme }) => theme.spacing.M16};

  ${media.medium} {
    padding: ${({ theme }) =>
      `0 ${theme.spacing.M24} ${theme.spacing.M24} ${theme.spacing.M24}`};
  }
  ${media.large} {
    scroll-margin: 0;
  }
`;

export const ReviewSummary = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

const TotalReviews = styled.div`
  ${({ theme }) => theme.fontSize.M14};
  color: ${({ theme }) => theme.colors.GREY_DARK};
`;

export const Review = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

const Header = styled(InfoTitle)`
  margin-bottom: ${({ theme }) => theme.spacing.S8};
`;

const SSimpleShowMoreInline = styled(SimpleShowMoreInline)`
  p {
    ${({ theme }) => theme.fontSize.M14};
    color: ${({ theme }) => theme.colors.BLACK};
  }
  button {
    ${({ theme }) => theme.fontSize.M14};
  }
`;

export const GoogleReviewsLink = styled.div`
  ${({ theme }) => theme.fontSize.M14};
`;

const SOpenOutlineIc = styled(OpenOutlineIc)`
  margin-left: ${({ theme }) => theme.spacing.S4};
  vertical-align: text-bottom;
`;

const SToolkitLink = styled(ToolkitLink)`
  vertical-align: bottom;
`;

function GoogleReviewPanel({
  title,
  googleReviews,
  cdnUrl,
  adId,
  reviewUrl = false,
  numberReviews = 3,
}: GoogleReviewPanelProps) {
  const firstThreeReviews = googleReviews?.reviewList.slice(0, numberReviews);

  return (
    <>
      <GoogleReviewContainer id="google-dealer-reviews">
        <ReviewSummary>
          <InfoTitle
            title={title}
            listItems={[
              <StarRatingGroup
                rating={parseFloat(googleReviews.rating)}
                starHeight="20"
                starWidth="20"
                key={1}
              />,

              <TotalReviews key={2}>
                {googleReviews.userRatingsTotal || 0} Google reviews
              </TotalReviews>,
            ]}
          />
        </ReviewSummary>

        <>
          {firstThreeReviews?.map((review: ReviewList, index: number) => (
            <Review key={index}>
              <Header
                illustration={`${cdnUrl}/images/illustrations/profile.svg`}
                title={review.authorName}
                variant="SMALL"
                key={index}
                listItems={[
                  <StarRatingGroup
                    rating={review.rating}
                    showRatingNumber={false}
                    starHeight="16"
                    starWidth="16"
                    key={index}
                  />,
                  `${review.relativeTimeDescription}`,
                ]}
              />

              <SSimpleShowMoreInline
                visibleText={review.text}
                showMoreText={'Read More'}
                readMoreDataTracking={'google-reviews-read-more'}
              />
            </Review>
          ))}
          <GoogleReviewsLink>
            <SToolkitLink
              href={
                reviewUrl && googleReviews.url
                  ? googleReviews.url
                  : `/adview/api/v1/log/clickedGoogleReview?adId=${adId}&type=review&cid=${googleReviews.cid}`
              }
              target="_blank"
              data-tracking={'visit-google-reviews-link'}
              rel="nofollow"
              fontSize="M14"
            >
              View Google Reviews
              <SOpenOutlineIc width="16" height="16" />
            </SToolkitLink>
          </GoogleReviewsLink>
        </>
      </GoogleReviewContainer>
    </>
  );
}

export { GoogleReviewPanel };
